import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Divider,
  Avatar,
  useColorModeValue,
  useToast,
  Skeleton,
  SimpleGrid,
  Tooltip
} from '@chakra-ui/react';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiExternalLink,
  FiCalendar,
  FiDollarSign,
  FiClipboard,
  FiClock
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';

// Services para obtener detalles completos de la actividad
import { getCustomer, getUser, getOrderDetails } from 'services/woo';
import { getUserAuth } from 'services/surveyService';
import WooCommerceRestApi from '@woocommerce/woocommerce-rest-api';
import { getSiteConfig } from 'utils/site';

const ModalActivityDetail = ({ activity, isOpen, onClose }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función auxiliar para crear instancia WooCommerce
  const createWooCommerceInstance = () => {
    const config = JSON.parse(getSiteConfig() || '{}');
    const getConfig = (config, key) => {
      const configItem = config.configs?.find(item => item.key === key);
      return configItem ? configItem.value : null;
    };

    return new WooCommerceRestApi({
      url: getConfig(config, "WOO_API_URL") || "https://capitalweb.cl",
      consumerKey: getConfig(config, "WOO_API_CONSUMER_KEY"),
      consumerSecret: getConfig(config, "WOO_API_CONSUMER_SECRET"),
      version: "wc/v3"
    });
  };

  // Función auxiliar para obtener customer correctamente con GET
  const getCustomerCorrect = async (customerId) => {
    const api = createWooCommerceInstance();
    try {
      const response = await api.get(`customers/${customerId}`);
      return response;
    } catch (error) {
      throw error;
    }
  };
  
  const toast = useToast();
  const history = useHistory();
  
  // Colores para modo claro/oscuro
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorder = useColorModeValue('gray.200', 'gray.500');

  // Función para obtener el ícono según el tipo de actividad
  const getActivityIcon = (type) => {
    switch (type) {
      case 'payment': return FiDollarSign;
      case 'user': return FiUser;
      case 'survey': return FiClipboard;
      case 'event': return FiCalendar;
      default: return FiClock;
    }
  };

  // Función para obtener el color según el tipo de actividad
  const getActivityColor = (type) => {
    switch (type) {
      case 'payment': return 'green';
      case 'user': return 'blue';
      case 'survey': return 'teal';
      case 'event': return 'purple';
      default: return 'gray';
    }
  };

  // Función para extraer user_id de la actividad según el tipo
  const getUserIdFromActivity = (activity) => {
    if (!activity?.data) return null;
    
    switch (activity.type) {
      case 'user':
        return activity.data.id;
      case 'payment':
        return activity.data.customer_id;
      case 'survey':
        return activity.data.respondent?.id;
      case 'event':
        return activity.data.created_by;
      default:
        return activity.data.user_id || activity.data.customer_id;
    }
  };

  // Función para obtener detalles completos de la actividad (siguiendo patrón de ModalOrderEdit)
  const fetchActivityDetails = async () => {
    if (!activity) return;

    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔍 MODAL: Obteniendo detalles completos para actividad tipo: ${activity.type}`);
      
      if (activity.type === 'payment') {
        // Para pagos, usar getOrderDetails como en ModalOrderEdit
        const paymentId = activity.data?.id;
        if (paymentId) {
          console.log(`💳 MODAL: Obteniendo detalles de pago ID: ${paymentId}`);
          const orderResponse = await getOrderDetails(paymentId);
          
          if (orderResponse.success && orderResponse.data?.customer_info) {
            console.log('✅ MODAL: Detalles de pago obtenidos con customer_info:', orderResponse.data.customer_info);
            setUserDetails({
              source: 'payment_order',
              data: orderResponse.data.customer_info,
              full_order: orderResponse.data
            });
            return;
          }
        }
      }
      
      // Para otros tipos de actividad, primero verificar si ya tenemos la data del usuario
      if (activity.type === 'user' && activity.data) {
        console.log('✅ MODAL: Actividad de usuario con data completa:', activity.data);
        setUserDetails({
          source: 'user_activity',
          data: activity.data
        });
        return;
      }
      
      // Para surveys, verificar si tenemos información del respondente
      if (activity.type === 'survey' && activity.data?.respondent) {
        console.log('✅ MODAL: Actividad de survey con respondente completo:', activity.data.respondent);
        setUserDetails({
          source: 'survey_respondent',
          data: activity.data.respondent,
          full_survey: activity.data
        });
        return;
      }
      
      // Para otros tipos, extraer user_id y buscar directamente
      const userId = getUserIdFromActivity(activity);
      console.log(`🔍 MODAL: User ID extraído: ${userId} para tipo ${activity.type}`);
      console.log(`🔍 MODAL: Datos de actividad completos:`, activity.data);
      
      if (userId) {
        // Intentar getCustomerCorrect primero (clientes WooCommerce)
        console.log(`🔍 MODAL: Intentando getCustomerCorrect con ID: ${userId}`);
        try {
          const customerResponse = await getCustomerCorrect(userId);
          console.log(`🔍 MODAL: Respuesta getCustomerCorrect:`, customerResponse);
          if (customerResponse && customerResponse.data && customerResponse.data.id) {
            console.log('✅ MODAL: Usuario encontrado como customer:', customerResponse.data);
            setUserDetails({
              source: 'woocommerce_customer',
              data: customerResponse.data
            });
            return;
          }
        } catch (customerError) {
          console.log('⚠️ MODAL: Error en getCustomerCorrect:', customerError);
        }

        // Si no es customer, intentar getUser (usuarios WordPress)
        console.log(`🔍 MODAL: Intentando getUser con ID: ${userId}`);
        try {
          const userResponse = await getUser(userId);
          console.log(`🔍 MODAL: Respuesta getUser:`, userResponse);
          if (userResponse && userResponse.id) {
            console.log('✅ MODAL: Usuario encontrado como user:', userResponse);
            setUserDetails({
              source: 'woocommerce_user',
              data: userResponse
            });
            return;
          }
        } catch (userError) {
          console.log('⚠️ MODAL: Error en getUser:', userError);
        }
      } else {
        console.log('⚠️ MODAL: No se pudo extraer user_id de la actividad');
        console.log('🔍 MODAL: Estructura completa de la actividad:', {
          type: activity.type,
          id: activity.id,
          data: activity.data,
          data_keys: Object.keys(activity.data || {})
        });
      }

      // Si no se encuentra
      setError(`No se pudo obtener información del usuario para actividad tipo ${activity.type}`);
      
    } catch (error) {
      console.error('❌ MODAL: Error obteniendo detalles de actividad:', error);
      setError('Error al obtener información de la actividad');
    } finally {
      setLoading(false);
    }
  };

  // Cargar detalles de la actividad cuando se abre el modal
  useEffect(() => {
    if (isOpen && activity) {
      fetchActivityDetails();
    }
    
    // Limpiar estado cuando se cierra el modal
    if (!isOpen) {
      setUserDetails(null);
      setLoading(false);
      setError(null);
    }
  }, [isOpen, activity]);

  // Función para navegar al perfil del usuario
  const navigateToProfile = () => {
    const userId = userDetails?.data?.id;
    if (userId) {
      onClose();
      history.push(`/admin/usuario/${userId}`);
    } else {
      toast({
        title: 'Error',
        description: 'No se pudo determinar el ID del usuario',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Función para abrir perfil en nueva ventana
  const openProfileInNewWindow = () => {
    const userId = userDetails?.data?.id;
    if (userId) {
      window.open(`/#/admin/usuario/${userId}`, '_blank');
    } else {
      toast({
        title: 'Error',
        description: 'No se pudo determinar el ID del usuario',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!activity) return null;

  const ActivityIcon = getActivityIcon(activity.type);
  const activityColor = getActivityColor(activity.type);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor} color={textColor}>
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
          <HStack spacing={3}>
            <Box
              p={2}
              bg={`${activityColor}.100`}
              borderRadius="lg"
            >
              <Icon as={ActivityIcon} boxSize={5} color={`${activityColor}.600`} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">
                {activity.title}
              </Text>
              <Text fontSize="sm" color={mutedTextColor}>
                Detalles de la actividad
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        
        <ModalCloseButton />
        
        <ModalBody py={6} className="custom-scrollbar" maxHeight="70vh" overflowY="auto">
          <VStack spacing={6} align="stretch">
            
            {/* Información de la actividad */}
            <Box
              p={4}
              border="1px"
              borderColor={cardBorder}
              borderRadius="lg"
              bg={cardBg}
            >
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="bold" color={mutedTextColor} textTransform="uppercase">
                  Información de la Actividad
                </Text>
                
                <SimpleGrid columns={2} spacing={3}>
                  <Box>
                    <Text fontSize="xs" color={mutedTextColor}>Tipo</Text>
                    <Badge colorScheme={activityColor} borderRadius="full">
                      {activity.type}
                    </Badge>
                  </Box>
                  
                  <Box>
                    <Text fontSize="xs" color={mutedTextColor}>Estado</Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {activity.status}
                    </Text>
                  </Box>
                  
                  <Box gridColumn="span 2">
                    <Text fontSize="xs" color={mutedTextColor}>Descripción</Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {activity.description}
                    </Text>
                  </Box>
                  
                  <Box gridColumn="span 2">
                    <Text fontSize="xs" color={mutedTextColor}>Fecha</Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {new Date(activity.timestamp).toLocaleString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Text>
                  </Box>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Información del usuario asociado */}
            <Box
              p={4}
              border="1px"
              borderColor={cardBorder}
              borderRadius="lg"
              bg={cardBg}
            >
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" fontWeight="bold" color={mutedTextColor} textTransform="uppercase">
                  Usuario Asociado
                </Text>

                {loading ? (
                  <VStack spacing={3}>
                    <Skeleton height="60px" width="60px" borderRadius="full" />
                    <Skeleton height="20px" width="200px" />
                    <Skeleton height="16px" width="150px" />
                  </VStack>
                ) : error ? (
                  <Box textAlign="center" py={4}>
                    <Text fontSize="sm" color="red.500" mb={2}>
                      {error}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={fetchActivityDetails}
                    >
                      Reintentar
                    </Button>
                  </Box>
                ) : userDetails ? (
                  <VStack spacing={4}>
                    {/* Avatar y nombre */}
                    <VStack spacing={2} textAlign="center">
                      <Avatar
                        size="lg"
                        name={`${userDetails.data.first_name || ''} ${userDetails.data.last_name || ''}`.trim() || userDetails.data.display_name || 'Sin nombre'}
                        bg={`${activityColor}.500`}
                      />
                      <VStack spacing={1}>
                        <Text fontSize="lg" fontWeight="bold">
                          {`${userDetails.data.first_name || ''} ${userDetails.data.last_name || ''}`.trim() || 
                           userDetails.data.display_name || 'Sin nombre'}
                        </Text>
                        <Badge colorScheme="blue" borderRadius="full" fontSize="xs">
                          ID: {userDetails.data.id}
                        </Badge>
                      </VStack>
                    </VStack>

                    <Divider borderColor={cardBorder} />

                    {/* Información del usuario */}
                    <VStack spacing={3} align="stretch" w="full">
                      {userDetails.data.email && (
                        <HStack spacing={3}>
                          <Icon as={FiMail} color={mutedTextColor} boxSize={3} />
                          <Text fontSize="xs" color={mutedTextColor} minW="50px">Email:</Text>
                          <Text fontSize="xs" fontWeight="medium" isTruncated>
                            {userDetails.data.email}
                          </Text>
                        </HStack>
                      )}
                      
                      {(userDetails.data.username || userDetails.data.rut) && (
                        <HStack spacing={3}>
                          <Icon as={FiUser} color={mutedTextColor} boxSize={3} />
                          <Text fontSize="xs" color={mutedTextColor} minW="50px">RUT:</Text>
                          <Text fontSize="xs" fontWeight="medium">
                            {userDetails.data.username || userDetails.data.rut || 'No disponible'}
                          </Text>
                        </HStack>
                      )}

                      {userDetails.data.role && (
                        <HStack spacing={3}>
                          <Icon as={FiUser} color={mutedTextColor} boxSize={3} />
                          <Text fontSize="xs" color={mutedTextColor} minW="50px">Rol:</Text>
                          <Text fontSize="xs" fontWeight="medium">
                            {userDetails.data.role}
                          </Text>
                        </HStack>
                      )}
                    </VStack>

                    <Divider borderColor={cardBorder} />

                    {/* Botones de acción */}
                    <VStack spacing={2} w="full">
                      <Button
                        leftIcon={<Icon as={FiUser} />}
                        colorScheme="blue"
                        size="sm"
                        w="full"
                        onClick={navigateToProfile}
                      >
                        Ver Perfil
                      </Button>
                      
                      <Button
                        leftIcon={<Icon as={FiExternalLink} />}
                        variant="outline"
                        size="sm"
                        w="full"
                        onClick={openProfileInNewWindow}
                      >
                        Abrir en Nueva Ventana
                      </Button>
                    </VStack>
                  </VStack>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Text fontSize="sm" color={mutedTextColor}>
                      No se pudo cargar información del usuario
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        
        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalActivityDetail;